import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, Plus, X } from 'lucide-react';
import {
  createOpenPlayJoin,
  discardUnpaidOpenPlayJoin,
  subscribeSessionJoins,
} from '../utils/openPlayUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { OPEN_PLAY_CHECKOUT_ROUTE_PATH } from '../constants/bookingConst';
import ConnectionErrorScreen from './ConnectionErrorScreen';
import CheckoutTabPending from './CheckoutTabPending';
import OpenPlaySessionDetails from './OpenPlaySessionDetails';
import OpenPlayRoster from './OpenPlayRoster';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import { useCheckoutTab } from '../hooks/useCheckoutTab';
import { useAuthStatus } from '../context/AuthContext';
import { hasReceipt } from '../utils/receiptUtil';
import { msUntil } from '../utils/countdownUtil';
import '../css/UserInfoForm.css';
import '../css/CheckoutSummary.css';
import '../css/OpenPlayModal.css';
import { formatMoney } from '../utils/currencyUtil';

const todayStr = () => new Date().toISOString().slice(0, 10);

// Rows are keyed by an id rather than by array index, because a middle row
// can be removed — React would otherwise reuse the wrong <input> and carry
// the deleted player's text into the row that took its place.
let playerRowSeq = 0;
const newPlayerRow = () => ({ id: `p${playerRowSeq += 1}`, name: '', phone: '' });

/**
 * Deletes a join that was never paid for, ignoring failures.
 * A leftover unpaid record is harmless — it counts for nothing and is
 * invisible to the admin — so no caller should be blocked by this failing.
 * @param {string|undefined} joinId
 * @returns {Promise<void>}
 */
const discardJoinQuietly = async (joinId) => {
  if (!joinId) return;
  try {
    await discardUnpaidOpenPlayJoin(joinId);
  } catch (err) {
    console.error('Could not discard the unpaid Open Play join:', err);
  }
};

/**
 * Guest-facing "Join Open Play" flow — pay-per-player rotating-queue
 * sessions, separate from reserving a whole court. Opened from an
 * "OPEN PLAY (Click to join)" cell on the booking grid or from a hosted
 * session's card (see App.jsx's handleJoinOpenPlaySlot /
 * handleOpenPlaySessionClick, and TimeSlotSelection.jsx).
 *
 * Three steps, deliberately mirroring the court-booking flow the guest
 * already knows — join details -> checkout -> confirmation:
 *
 *   'details'  the session summary, the list of players coming, a live
 *              joined/cap counter and the running total
 *   'payment'  the QR + countdown + receipt upload now open in their own
 *              tab (OpenPlayCheckoutPage); this modal shows a "complete your
 *              payment" card meanwhile and moves on when that tab reports back
 *   'done'     confirmation, keyed by the join's OP-… reference
 *
 * Joins are written to `openPlayJoins`, NOT `bookings`: guest availability
 * is derived from bookings' timeSlots (buildBookedSlotsFromSnapshot), so a
 * booking record here would flip the cell to "Booked" and lock every other
 * guest out of the session it was advertising. The admin reviews these under
 * Admin -> Open Play, and they also feed the host modal's player list.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {object} config - app config (payment QR codes, paymentTimeoutMinutes)
 * @param {Array<{id:string,name:string,icon:string,openPlayPricePerPlayer:number,openPlayNote:string}>} sports
 * @param {string|null} initialSportId - the sport the clicked cell/card belongs to
 * @param {string|null} initialDate - "yyyy-MM-dd" of the clicked cell/session
 * @param {string|null} initialTimeSlot - the clicked cell's slot (e.g. "5-6PM"),
 *   null when opened from a session card, which covers many slots at once
 * @param {object|null} session - the admin-hosted openPlaySessions record, if
 *   any. Its fee/notes/maxPlayers take priority over the sport's generic
 *   openPlayPricePerPlayer/openPlayNote, and its cap is enforced.
 */
function OpenPlayModal({ isOpen, onClose, config, sports, initialSportId = null, initialDate = null, initialTimeSlot = null, session = null }) {
  // Can open from the landing page while sign-in is still in flight.
  const { ready: authReady } = useAuthStatus();
  // Defined per component rather than at module scope: the symbol comes from
  // Config -> System, so it needs `config` in hand. Call sites read the same
  // as they always did.
  const peso = (n) => formatMoney(n, config);
  const [step, setStep] = useState('details'); // 'details' | 'payment' | 'done'
  const [sportId, setSportId] = useState(initialSportId || sports?.[0]?.id || '');
  const [date, setDate] = useState(initialDate || todayStr());
  // Everyone this guest is bringing, themselves included. The first row is
  // the person doing the booking — the only one whose contact number is
  // required, since that's who the club calls back.
  const [players, setPlayers] = useState([newPlayerRow()]);
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [join, setJoin] = useState(null);

  // Checkout (payment) runs in its own tab. What it reports back moves this
  // modal on. Handlers are re-created each render, so they read the current
  // `join`.
  const checkoutTab = useCheckoutTab({
    // Receipt stored in the checkout tab — the join is submitted.
    onReceiptUploaded: (updatedJoin) => {
      if (updatedJoin) setJoin(updatedJoin);
    },
    // "Done" in the checkout tab — show the confirmation.
    onSubmitted: (updatedJoin) => {
      if (updatedJoin) setJoin(updatedJoin);
      setStep('done');
    },
    // Back / Start Over in the checkout tab (the unpaid join is already
    // discarded there) — back to the untouched player form.
    onCancelled: () => {
      setJoin(null);
      setStep('details');
    },
    onExpired: () => {
      setJoin(null);
      setStep('details');
    },
    // Closed the checkout tab without tapping Done. A paid join still gets
    // its confirmation; an expired unpaid one sends the guest back to the
    // form; otherwise the card offers to reopen the tab.
    onTabClosed: () => {
      if (hasReceipt(join)) {
        checkoutTab.end();
        setStep('done');
        return;
      }
      const remaining = msUntil(join?.paymentDeadline);
      if (remaining !== null && remaining <= 0) {
        discardJoinQuietly(join?.id);
        checkoutTab.end();
        setJoin(null);
        setFormError('Your payment window has expired. Please try again.');
        setStep('details');
      }
    },
  }, { routePath: OPEN_PLAY_CHECKOUT_ROUTE_PATH });

  // Clean slate every time the modal is (re)opened.
  useEffect(() => {
    if (!isOpen) return;
    setStep('details');
    setSportId(initialSportId || sports?.[0]?.id || '');
    setDate(initialDate || todayStr());
    setPlayers([newPlayerRow()]);
    setEmail('');
    setFormError('');
    setJoin(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSportId, initialDate, initialTimeSlot, session?.id]);

  const selectedSport = sports?.find(s => s.id === sportId) || sports?.[0];
  const sportName = session?.sportName || selectedSport?.name || 'Court';
  // A hosted session's own fee overrides the sport-level default — 0 is a
  // valid, deliberately-set fee, so this checks for the session existing
  // rather than falsy-coalescing.
  const pricePerPlayer = session ? (Number(session.fee) || 0) : (Number(selectedSport?.openPlayPricePerPlayer) || 0);
  const playerCount = players.length;
  const total = pricePerPlayer * playerCount;

  // Live joined count, so a session that fills up while this modal is open
  // stops accepting the guest's Continue rather than failing at write time.
  // Subscribed rather than fetched once for the same reason.
  const [joinedCount, setJoinedCount] = useState(0);
  // Names of everyone already on the session. Open Play is a social
  // rotation, so who else is coming is the thing a guest actually wants to
  // know before committing — the bare "12 / 30 joined" counter said nothing
  // about that.
  //
  // NAMES AND REVIEW STATE ONLY, deliberately. The join records also carry
  // phone numbers and emails, and this list is visible to anyone who can
  // open the modal; a roster is worth showing, a contact list is not.
  //
  // [{ name, status }] — 'confirmed' is on the session for good, 'pending'
  // has paid and is waiting on the admin (a walk-in the admin entered is
  // written straight to 'confirmed', so it needs no special case here).
  const [joinedPlayers, setJoinedPlayers] = useState([]);
  // How many of the players holding a spot are paid-but-not-yet-verified.
  // A full session is not necessarily a settled one: these joins are counted
  // (the money is in) but the admin hasn't confirmed them, and declining one
  // hands its spots straight back. Surfacing the number turns "full, go
  // away" into "full for now, worth checking back".
  const [pendingPlayerCount, setPendingPlayerCount] = useState(0);
  // True until the first joins snapshot arrives (CLAUDE.md > Quality >
  // Smooth Display). Without it the modal opened showing "0 / 30 joined"
  // and an empty roster for as long as the read took, which isn't a loading
  // state — it's a wrong answer that then changes under the guest.
  const [loadingJoins, setLoadingJoins] = useState(false);
  // Bumped by Try Again to tear the joins subscription down and open a fresh
  // one — subscribeSessionJoins on an unreachable database never fires its
  // callback, so re-subscribing is the only real retry.
  const [joinsAttempt, setJoinsAttempt] = useState(0);
  const { timedOut: joinsTimedOut, restart: restartJoinsTimeout } = useLoadingTimeout(loadingJoins);

  /**
   * Try Again on the capacity timeout — restarts the countdown and
   * re-subscribes to this session's joins.
   *
   * @returns {void}
   */
  const handleJoinsRetry = () => {
    restartJoinsTimeout();
    setJoinsAttempt((n) => n + 1);
  };

  useEffect(() => {
    if (!isOpen || !session?.id) {
      setJoinedCount(0);
      setJoinedPlayers([]);
      setPendingPlayerCount(0);
      setLoadingJoins(false);
      return undefined;
    }
    setLoadingJoins(true);
    const unsubscribe = subscribeSessionJoins(session.id, (joins) => {
      setLoadingJoins(false);
      setJoinedCount(joins.reduce((sum, j) => sum + (Number(j.numPlayers) || 0), 0));
      setPendingPlayerCount(joins
        .filter(j => j.status === 'pending')
        .reduce((sum, j) => sum + (Number(j.numPlayers) || 0), 0));
      setJoinedPlayers(joins.flatMap(j => {
        // A join saved before rosters were stored has only its booker.
        const names = j.players?.length
          ? j.players.map(p => p.name).filter(Boolean)
          : [j.fullName].filter(Boolean);
        // Every player on one join shares that join's review state.
        return names.map(name => ({ name, status: j.status }));
      }));
    });
    return () => unsubscribe();
  }, [isOpen, session?.id, joinsAttempt]);

  const maxPlayers = session ? (Number(session.maxPlayers) || 0) : 0;
  // Infinity for a cell with no hosted session behind it — there is no cap
  // to enforce there, same as before hosted sessions existed.
  const capacityRemaining = session ? Math.max(0, maxPlayers - joinedCount) : Infinity;
  const sessionFull = session ? capacityRemaining <= 0 : false;
  const overCapacity = session ? playerCount > capacityRemaining : false;
  // What the counter shows: everyone already in, plus everyone this guest is
  // about to add. Updates as rows are added or removed.
  const projectedCount = joinedCount + playerCount;

  if (!isOpen) return null;

  // Deliberately NOT closed by a backdrop click (CLAUDE.md > Behavior).
  // This modal holds a typed roster, contact details and, on the checkout
  // step, a live payment countdown — a stray tap outside used to throw all
  // of it away with no confirmation. The × in the header is the way out.

  const updatePlayer = (id, field, value) => {
    setFormError('');
    setPlayers(prev => prev.map(p => (p.id === id ? { ...p, [field]: value } : p)));
  };

  const addPlayer = () => {
    setFormError('');
    setPlayers(prev => [...prev, newPlayerRow()]);
  };

  // The first row is the booker and can't be removed — removing it would
  // silently promote someone else into "the person we call back", whose
  // contact number is optional and probably blank.
  const removePlayer = (id) => {
    setFormError('');
    setPlayers(prev => (prev.length > 1 ? prev.filter(p => p.id !== id) : prev));
  };

  /**
   * "Back to players" on the payment card -> back to the player form.
   *
   * The join record was already written when Continue was pressed, so it has
   * to be thrown away here — otherwise pressing Continue again writes a
   * SECOND record and the club sees the same guest twice.
   * discardUnpaidOpenPlayJoin re-checks the stored status, so a join paid in
   * the checkout tab meanwhile is never deleted.
   *
   * `players` state is untouched, so the form comes back exactly as it was
   * left. The checkout tab is closed first.
   */
  const handleBackToPlayers = () => {
    if (hasReceipt(join)) {
      checkoutTab.end();
      setStep('done');
      return;
    }
    const joinId = join?.id;
    checkoutTab.end();
    setJoin(null);
    setStep('details');
    discardJoinQuietly(joinId);
  };

  /**
   * The payment countdown ran out on the payment card. While the checkout tab
   * is open it shows its own "expired" screen with Start Over, so nothing
   * happens here; with no tab open this does what Start Over would.
   */
  const handlePaymentDeadline = () => {
    if (checkoutTab.isCheckoutTabOpen()) return;
    handleBackToPlayers();
    setFormError('Your payment window has expired. Please try again.');
  };

  /**
   * The modal's × — closes the checkout tab too, and discards the join if it
   * was never paid for.
   */
  const handleClose = () => {
    if (step === 'payment') {
      checkoutTab.end();
      if (!hasReceipt(join)) discardJoinQuietly(join?.id);
    }
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    // The join write is denied until sign-in finishes. The button is disabled
    // too — this guards the Enter-key submit path.
    if (!authReady) return;

    if (!selectedSport && !session) {
      setFormError('No Open Play sport is available right now.');
      return;
    }

    const named = players.map(p => ({ ...p, name: p.name.trim(), phone: p.phone.trim() }));
    const missingName = named.findIndex(p => !p.name);
    if (missingName !== -1) {
      setFormError(missingName === 0
        ? 'Please enter your name.'
        : `Please enter a name for player ${missingName + 1}, or remove that row.`);
      return;
    }
    if (!named[0].phone) {
      setFormError('A contact number is required for the first player.');
      return;
    }
    if (sessionFull) {
      setFormError('This session is already full.');
      return;
    }
    if (overCapacity) {
      setFormError(`Only ${capacityRemaining} spot${capacityRemaining === 1 ? '' : 's'} left in this session — remove ${playerCount - capacityRemaining} player${playerCount - capacityRemaining === 1 ? '' : 's'} to continue.`);
      return;
    }

    // Opened here, synchronously inside the tap — browsers only allow
    // window.open during a user gesture, and the gesture is gone once the
    // join has been written. The tab shows a spinner until launch() below
    // hands it the join, and is closed again if the write fails.
    checkoutTab.prepare();
    setSubmitting(true);
    try {
      const record = await createOpenPlayJoin({
        sportId: session?.sportId || selectedSport.id,
        sportName,
        date: session?.date || date,
        timeSlot: initialTimeSlot || null,
        sessionId: session?.id || null,
        // The booker's own details stay in the top-level fields every other
        // screen already reads (Admin -> Open Play, the host modal's player
        // list); the full roster rides alongside them.
        fullName: named[0].name,
        email: email.trim(),
        phone: named[0].phone,
        players: named.map(p => ({ name: p.name, phone: p.phone })),
        numPlayers: named.length,
        pricePerPlayer,
      }, config);
      setJoin(record);
      setStep('payment');
      checkoutTab.launch(record, [record.id], {
        session,
        sportName,
        pricePerPlayer,
        dateStr: session?.date || date,
        timeSlot: initialTimeSlot || null,
      });
    } catch (err) {
      checkoutTab.end();
      setFormError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const detailsProps = {
    session,
    config,
    sportName,
    dateStr: session?.date || date,
    timeSlot: initialTimeSlot,
    pricePerPlayer,
  };

  return createPortal(
    <div className="opm-overlay">
      <div className="opm-scroll">
        {step === 'payment' && join ? (
          <div className="opm-card-wrap">
            <CheckoutTabPending
              bookingData={join}
              tabState={checkoutTab.tabState}
              onOpenCheckout={checkoutTab.reopen}
              onCancel={handleBackToPlayers}
              onClose={handleClose}
              onExpire={handlePaymentDeadline}
              submitLabel="Done"
              cancelLabel="Back to players"
              referenceLabel="Reference"
              expiryNote="or you'll need to submit again."
              closedNote="Your spot isn’t held until you upload your receipt — reopen it to finish your payment."
            />
          </div>
        ) : (
        <div className="user-info-form checkout-summary opm-card-wrap">
          <div className="uif-card cs-card">
            <div className="uif-head">
              <h2 className="uif-title">
                {step === 'details' ? 'Join this session' : 'Booking confirmed'}
              </h2>
              <button type="button" className="uif-close" onClick={handleClose} aria-label="Close">&times;</button>
            </div>

            {step === 'details' && (
              <form onSubmit={handleSubmit}>
                <OpenPlaySessionDetails {...detailsProps} showNotes />

                {/* ---- Who's coming ---- */}
                <div className="opm-players">
                  <div className="opm-players-head">
                    <label className="opm-section-label">Players</label>
                    <span className="opm-players-hint">
                      Contact number is only required for the first player.
                    </span>
                  </div>

                  {players.map((player, index) => (
                    <div className="opm-player-row" key={player.id}>
                      <div className="opm-player-num">{index + 1}</div>
                      <input
                        type="text"
                        className="opm-player-name"
                        value={player.name}
                        onChange={(e) => updatePlayer(player.id, 'name', e.target.value)}
                        placeholder={index === 0 ? 'Your name' : `Player ${index + 1}`}
                        aria-label={index === 0 ? 'Your name' : `Player ${index + 1} name`}
                      />
                      {/* Placeholder is short on the later rows: that field
                          is the narrower of the two and now ends under the ×,
                          so "Contact (optional)" was cut mid-word. The
                          aria-label still says it is optional. */}
                      <input
                        type="tel"
                        className="opm-player-phone"
                        value={player.phone}
                        onChange={(e) => updatePlayer(player.id, 'phone', e.target.value)}
                        placeholder={index === 0 ? '0917…' : 'Contact'}
                        aria-label={index === 0 ? 'Your contact number' : `Player ${index + 1} contact number (optional)`}
                      />
                      {/* No placeholder for the first row any more: the ×
                          floats over the right end of the phone field (see
                          OpenPlayModal.css), so every row's fields run the
                          full width and line up with each other and with the
                          Add player button. */}
                      {index > 0 && (
                        <button
                          type="button"
                          className="opm-player-remove"
                          onClick={() => removePlayer(player.id)}
                          aria-label={`Remove player ${index + 1}`}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    className="opm-add-player"
                    onClick={addPlayer}
                    disabled={sessionFull}
                  >
                    <Plus size={14} /> Add player
                  </button>
                </div>

                <div className="form-group">
                  <label htmlFor="op-email">Email Address <span className="uif-optional">(optional)</span></label>
                  <input
                    type="email"
                    id="op-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="juandelacruz@email.com"
                  />
                </div>

                {/* ---- Live capacity ---- */}
                {session && loadingJoins && !joinsTimedOut && (
                  <div className="opm-loading">
                    {spinnerRing('Checking who has joined…')}
                  </div>
                )}

                {/* Capacity never arrived. Showing the error here rather than
                    letting the counter render is deliberate: "0 / 30 joined"
                    on a stalled read is a wrong answer, not a loading state,
                    and a guest could join a session that is already full. */}
                {session && joinsTimedOut && (
                  <ConnectionErrorScreen onRetry={handleJoinsRetry} />
                )}

                {session && !loadingJoins && !joinsTimedOut && (
                  <div className={`opm-capacity${overCapacity || sessionFull ? ' over' : ''}`}>
                    <div className="opm-capacity-count">
                      <strong>{projectedCount}</strong> / {maxPlayers} joined
                    </div>
                    {/* Just the arithmetic. When the guest is blocked, the
                        reason and what to do about it belong in the notice
                        below — saying "only 1 spot left" in both places
                        made the explanation look like a repeat. */}
                    <div className="opm-capacity-sub">
                      {joinedCount} already joined &middot; you&rsquo;re adding {playerCount}
                    </div>
                  </div>
                )}

                {/* Why the guest is blocked, and whether it's worth waiting.
                    Shown for BOTH ways capacity stops them — a full session,
                    and a party bigger than the spots left. It used to be
                    full-only, which meant the common case (9 of 10 taken,
                    guest adding 2) got the bare "only 1 spot left" and no
                    hint that anything might change.

                    The reassurance is real, not padding: everyone counted
                    here has paid, but a 'pending' join is still awaiting the
                    admin's check, and declining one hands its spots straight
                    back. A guest told that comes back later; a guest who
                    just reads "full" doesn't. */}
                {session && !loadingJoins && !joinsTimedOut && (sessionFull || overCapacity) && (
                  <p className="cs-note cs-note-amber opm-full-note">
                    {sessionFull ? (
                      <>Max number of players is reached</>
                    ) : (
                      <>
                        Only {capacityRemaining} spot{capacityRemaining === 1 ? '' : 's'} left and
                        you&rsquo;re adding {playerCount}
                      </>
                    )}
                    {pendingPlayerCount > 0 ? (
                      <>
                        {/* Counted against everyone actually registered
                            (confirmed + pending = joinedCount), not the
                            cap. On a session that isn't full those differ,
                            and "3 of the 10" while only 9 have registered
                            reads as a number pulled from nowhere. */}
                        , but {pendingPlayerCount} of the {joinedCount} registered
                        player{pendingPlayerCount === 1 ? ' is' : 's are'} still waiting to be verified by the
                        admin. Don&rsquo;t worry, we&rsquo;ll open those slots once we don&rsquo;t receive their
                        payments.
                      </>
                    ) : (
                      // Nothing pending means every spot is confirmed, so
                      // there is no "check back later" to offer — saying it
                      // anyway would be a promise the session can't keep.
                      // Point at the one thing the guest can actually do.
                      <>
                        . Every spot on this session is confirmed
                        {overCapacity && !sessionFull
                          ? `, so you'd need to remove ${playerCount - capacityRemaining} player${playerCount - capacityRemaining === 1 ? '' : 's'} to continue.`
                          : '.'}
                      </>
                    )}
                  </p>
                )}

                {/* Who's already coming — see joinedPlayers. Each row carries
                    the join's review state, the same PENDING / CONFIRMED
                    marker the admin's own player list uses, so a guest can
                    tell a settled roster from one still being verified. */}
                {session && !loadingJoins && !joinsTimedOut && joinedPlayers.length > 0 && (
                  <div className="opm-joined">
                    <div className="opm-section-label">Already joined ({joinedPlayers.length})</div>
                    <div className="opm-joined-names">
                      {joinedPlayers.map((player, index) => (
                        // Names repeat across groups, so the key is the
                        // position — the list re-renders whole on each update.
                        <div className="opm-joined-chip" key={index}>
                          <span className="opm-joined-name">{player.name}</span>
                          <span className={`opm-joined-tag opm-joined-tag-${player.status === 'confirmed' ? 'confirmed' : 'pending'}`}>
                            {player.status === 'confirmed' ? 'CONFIRMED' : 'PENDING'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="uif-total-row">
                  <span>Total ({playerCount} &times; {peso(pricePerPlayer)})</span>
                  <span className="uif-total-amt">{peso(total)}</span>
                </div>

                {formError && <p className="cs-upload-error" style={{ textAlign: 'left' }}>{formError}</p>}

                <button type="submit" className="uif-continue" disabled={submitting || sessionFull || !authReady}>
                  {submitting || !authReady ? 'Please wait…' : sessionFull ? 'Session full' : 'Continue'}
                </button>
              </form>
            )}

            {step === 'done' && join && (
              <div className="opm-done">
                <div className="opm-done-icon"><Check size={28} /></div>
                <h3>You're on the list!</h3>
                <p>
                  We've received your Open Play request. {config?.courtName || 'We'} will confirm your spot
                  once your payment has been verified.
                </p>

                <OpenPlaySessionDetails {...detailsProps} showNotes={false} />

                {/* The same roster the checkout step showed — this screen is
                    the one a guest screenshots and shows at the door. */}
                <OpenPlayRoster join={join} />

                <div className="opm-done-rows">
                  <div className="opm-done-row">
                    <span>Booked by</span>
                    <span>{join.fullName}</span>
                  </div>
                  <div className="opm-done-row opm-done-total">
                    <span>Total paid</span>
                    <span>{peso(join.totalAmount)}</span>
                  </div>
                </div>

                <div className="cs-ref-block">
                  <div className="cs-ref-label">Reference</div>
                  <div className="cs-ref-value">{join.confirmationId}</div>
                  <div className="cs-ref-note">Keep this in case you need to follow up with us.</div>
                </div>

                <button type="button" className="uif-continue" onClick={handleClose}>Close</button>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default OpenPlayModal;
